import { InputHTMLAttributes, forwardRef } from 'react';
import { clsx } from 'clsx';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, className, ...props }, ref) => {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-semibold text-primary-300 mb-2">
            {label}
          </label>
        )}
        <input
          ref={ref}
          className={clsx(
            'w-full px-4 py-2 rounded-lg border bg-dark-50 text-primary-200',
            'focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent',
            'placeholder:text-primary-200/40',
            'transition-all duration-200',
            error
              ? 'border-error-600 focus:ring-error-600'
              : 'border-dark-200',
            props.disabled && 'opacity-50 cursor-not-allowed',
            className
          )}
          {...props}
        />
        {error && (
          <p className="mt-1 text-sm text-error-400">{error}</p>
        )}
        {helperText && !error && (
          <p className="mt-1 text-sm text-primary-300/70">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

interface TextareaProps extends InputHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  rows?: number;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, helperText, rows = 4, className, ...props }, ref) => {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-semibold text-primary-300 mb-2">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          rows={rows}
          className={clsx(
            'w-full px-4 py-2 rounded-lg border bg-dark-50 text-primary-200',
            'focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent',
            'placeholder:text-primary-200/40',
            'transition-all duration-200 resize-none',
            error
              ? 'border-error-600 focus:ring-error-600'
              : 'border-dark-200',
            className
          )}
          {...props as any}
        />
        {error && (
          <p className="mt-1 text-sm text-error-400">{error}</p>
        )}
        {helperText && !error && (
          <p className="mt-1 text-sm text-primary-300/70">{helperText}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
